import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { bracketMatching } from '@codemirror/language'
import { highlightSelectionMatches, search, searchKeymap } from '@codemirror/search'
import { Compartment, EditorState, Prec } from '@codemirror/state'
import {
  EditorView,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder as placeholderExtension,
  rectangularSelection,
} from '@codemirror/view'
import type { LanguageId } from '../shared/types'
import { languageExtension } from './languages'
import { editorTheme } from './sauces'

const languageSlot = new Compartment()
const themeSlot = new Compartment()

export interface EditorOptions {
  parent: HTMLElement
  doc: string
  language: LanguageId
  readOnly?: boolean
  placeholder?: string
  onChange?: (doc: string) => void
  onSubmit?: () => void
}

export interface EditorHandle {
  view: EditorView
  setLanguage: (id: LanguageId) => void
  refreshTheme: () => void
  destroy: () => void
}

export function createEditor(options: EditorOptions): EditorHandle {
  const readOnly = options.readOnly === true

  const submitKeymap = Prec.high(
    keymap.of([
      {
        key: 'Mod-Enter',
        run: () => {
          options.onSubmit?.()
          return true
        },
      },
      {
        key: 'Mod-s',
        preventDefault: true,
        run: () => {
          options.onSubmit?.()
          return true
        },
      },
    ]),
  )

  const view = new EditorView({
    parent: options.parent,
    state: EditorState.create({
      doc: options.doc,
      extensions: [
        submitKeymap,
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightActiveLine(),
        drawSelection(),
        dropCursor(),
        rectangularSelection(),
        bracketMatching(),
        highlightSelectionMatches(),
        history(),
        search({ top: true }),
        keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap, indentWithTab]),
        EditorView.lineWrapping,
        EditorState.allowMultipleSelections.of(true),
        options.placeholder ? placeholderExtension(options.placeholder) : [],
        readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : [],
        languageSlot.of([]),
        themeSlot.of(editorTheme()),
        options.onChange
          ? EditorView.updateListener.of((update) => {
              if (update.docChanged) options.onChange?.(update.state.doc.toString())
            })
          : [],
      ],
    }),
  })

  let pending = 0
  const setLanguage = (id: LanguageId): void => {
    const request = ++pending
    void languageExtension(id).then((extension) => {
      // A slower grammar resolving late must not clobber a newer selection.
      if (request !== pending) return
      view.dispatch({ effects: languageSlot.reconfigure(extension) })
    })
  }

  setLanguage(options.language)

  return {
    view,
    setLanguage,
    refreshTheme: () => view.dispatch({ effects: themeSlot.reconfigure(editorTheme()) }),
    destroy: () => view.destroy(),
  }
}
